import { Dayjs } from "dayjs";
import styled from "@emotion/styled";
import { ArrowDropDown, ChevronLeft, ChevronRight } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { PickersCalendarHeaderProps } from "@mui/x-date-pickers/PickersCalendarHeader";
import { PickersDay, PickersDayProps } from "@mui/x-date-pickers/PickersDay";

interface BaseCalendarProps {
  selectedDate: Dayjs;
  onDateChange: (date: Dayjs) => void;
  onMonthChange: (date: Dayjs) => void;
  CustomDay?: (day: Dayjs) => {
    hasSchedule?: boolean;
    [key: string]: any;
  };
  CustomHeader?: React.ComponentType<PickersCalendarHeaderProps<Dayjs>>;
  CustomFooter?: React.ComponentType;
}

export const BaseCalendar = ({
  selectedDate,
  onDateChange,
  onMonthChange,
  CustomDay,
  CustomHeader,
  CustomFooter,
}: BaseCalendarProps) => {
  const DefaultDay = (props: PickersDayProps<Dayjs>) => {
    const { day, ...otherProps } = props;
    const isSunday = day.day() === 0;

    const customProps = CustomDay ? CustomDay(day) : {};
    const { sx: customSx, ...otherCustomProps } = customProps;

    return (
      <StyledPickersDay
        {...otherProps}
        day={day}
        sx={{
          ...customSx,
          ...(otherProps.sx || {}),
        }}
        {...otherCustomProps}
        hasSchedule={customProps.hasSchedule}
        isSunday={isSunday}
      />
    );
  };

  const DefaultHeader = (props: PickersCalendarHeaderProps<Dayjs>) => {
    const {
      currentMonth,
      onMonthChange: handleMonthChange,
      onViewChange,
    } = props;

    const handlePreviousMonth = () => {
      const prevMonth = currentMonth.subtract(1, "month");
      handleMonthChange(prevMonth, "right");
      onMonthChange(prevMonth);
    };

    const handleNextMonth = () => {
      const nextMonth = currentMonth.add(1, "month");
      handleMonthChange(nextMonth, "left");
      onMonthChange(nextMonth);
    };

    return (
      <CustomCalendarHeader>
        <MonthSelector onClick={() => onViewChange?.("month")}>
          {currentMonth.format("MMMM YYYY")}
          <ArrowDropDown />
        </MonthSelector>
        <NavigationButtons>
          <IconButton
            onClick={handlePreviousMonth}
            size="small"
          >
            <ChevronLeft />
          </IconButton>
          <IconButton
            onClick={handleNextMonth}
            size="small"
          >
            <ChevronRight />
          </IconButton>
        </NavigationButtons>
      </CustomCalendarHeader>
    );
  };

  return (
    <CalendarWrapper>
      <LocalizationProvider
        dateAdapter={AdapterDayjs}
        adapterLocale="ko"
      >
        <StyledDateCalendar
          value={selectedDate}
          onChange={(newValue) => {
            if (newValue && newValue.isValid()) {
              onDateChange(newValue);
            }
          }}
          views={["month", "day"]}
          openTo="day"
          slots={{
            day: DefaultDay,
            calendarHeader: CustomHeader || DefaultHeader,
          }}
          showDaysOutsideCurrentMonth={false}
          dayOfWeekFormatter={(day) =>
            day.format("dd").toUpperCase().slice(0, 1)
          }
        />
        {CustomFooter && <CustomFooter />}
      </LocalizationProvider>
    </CalendarWrapper>
  );
};

export const CustomCalendarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 12px 8px 24px;
  min-height: 58px;
`;

export const MonthSelector = styled.div`
  display: flex;
  align-items: center;
  min-width: 192px;
  height: 34px;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;

  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }

  svg {
    margin-left: 4px;
  }

  font-size: 16px;
  font-weight: 500;
  line-height: 22px;
  letter-spacing: -0.4000000059604645px;
  text-align: left;
  text-underline-position: from-font;
  text-decoration-skip-ink: none;
  color: #000000de;
`;

export const NavigationButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  width: 92px;
  height: 33px;
`;

export const CalendarWrapper = styled.div`
  width: 320px;
  min-width: 320px;
  max-width: 320px;
  border-radius: 4px 0 0 0;
  border: 1px solid rgba(0, 0, 0, 0.12);
  background: white;
  overflow: hidden;
`;

export const StyledPickersDay = styled(PickersDay, {
  shouldForwardProp: (prop) =>
    !["hasSchedule", "isSunday"].includes(prop as string),
})<{
  hasSchedule?: boolean;
  isSunday?: boolean;
}>`
  width: 36px !important;
  height: 36px !important;
  margin: 0;
  border-radius: 100px !important;

  /* 일요일과 스케줄 상태를 통합한 color 스타일 */
  ${({ theme, isSunday, hasSchedule }) => {
    let color = "inherit";
    if (isSunday) color = (theme as any).palette?.text?.disabled;
    if (hasSchedule) color = (theme as any).palette?.primary?.main;
    return `color: ${color} !important;`;
  }}

  /* 오늘 날짜 */
  &.MuiPickersDay-today {
    border: 1px solid rgba(0, 0, 0, 0.56) !important;
    background: transparent !important;
  }

  /* 선택된 날짜 */
  &.Mui-selected {
    background-color: ${({ theme }) =>
      (theme as any).palette?.primary?.main} !important;
    color: white !important;
    border: none !important;

    &:hover {
      background-color: ${({ theme }) =>
        (theme as any).palette?.primary?.dark} !important;
    }
  }
`;

export const StyledDateCalendar = styled(DateCalendar)`
  height: 100%;

  /* 요일 헤더 컨테이너 */
  .MuiDayCalendar-header {
    width: 100%;
    height: 36px;
    gap: 2px;
    margin: 0;
  }

  /* 요일 헤더 개별 텍스트 */
  .MuiDayCalendar-weekDayLabel {
    width: 36px;
    height: 36px;
    margin: 0;
    display: flex;
    align-items: center;
    justify-content: center;

    /* 텍스트 스타일링 */
    font-family: Roboto;
    font-size: 14px;
    font-weight: 400;
    line-height: 20.02px;
    letter-spacing: 0.17px;
    color: rgba(0, 0, 0, 0.6); // --text-secondary, #00000099
    text-align: center;
    // text-underline-position: from-font;
    // text-decoration-skip-ink: none;
  }

  /* 날짜 그리드 */
  .MuiDayCalendar-monthContainer {
    margin: 0;
  }

  .MuiDayCalendar-weekContainer {
    margin: 0;
    gap: 2px;

    font-family: Roboto;
    font-size: 14px;
    font-weight: 400;
    line-height: 20.02px;
    letter-spacing: 0.17px;
    text-align: center;
  }

  /* 캘린더 paper 스타일 제거 */
  .MuiPaper-root {
    box-shadow: none;
  }
`;
